import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Eye, EyeOff, Info } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSignIn } from '@/api/auth/sign-in';
import { isSessionValid, saveAuthContext } from '@/utils/router-auth';
import { storage } from '@/utils/storage';
import { useGetInvitationByToken } from '@/api/organizations/get-invitation-by-token';

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  invitationToken: z.string().optional()
});

export const Route = createFileRoute('/login/')({
  validateSearch: loginSearchSchema,
  component: RouteComponent
});

function RouteComponent() {
  const navigate = useNavigate();
  const { redirect, invitationToken } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const signInMutation = useSignIn();

  const { data: invitation } = useGetInvitationByToken(invitationToken || null);

  useEffect(() => {
    if (isSessionValid()) {
      navigate({ to: redirect || '/ideas' });
    }
  }, [navigate, redirect]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    signInMutation.mutate(
      { email, password, invitationToken },
      {
        onSuccess: async (data) => {
          saveAuthContext(
            {
              email: data.email,
              firstName: '',
              lastName: '',
              company: '',
              role: data.role,
              defaultOrganizationId: data.defaultOrganizationId
            },
            {
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              accessTokenValidUntil: data.accessTokenValidUntil,
              refreshTokenValidUntil: data.refreshTokenValidUntil
            }
          );

          if (data.defaultProjectId) {
            storage.setProjectId(data.defaultProjectId);
            storage.setHasCompletedOnboarding(true);
          }

          if (invitationToken) {
            toast.success(`Vous avez rejoint l'organisation ${invitation?.organizationName || ''}`);
          }
          await navigate({ to: redirect || '/ideas' });
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : '';
          const status = (error as { status?: number }).status;
          const isUnauthorized = status === 401 || message.toLowerCase().includes('unauthorized');

          toast.error(
            isUnauthorized
              ? 'Échec de la connexion : email ou mot de passe incorrect.'
              : `Échec de la connexion : ${message || 'Erreur inconnue.'}`
          );
        }
      }
    );
  };

  return (
    <div className="h-dvh bg-linear-to-b from-background to-muted/20 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="group-hover:scale-110 transition-transform duration-300">
              <img src="/bespark-ai.webp" alt="BeSpark" className="h-14 w-14" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">
            <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">
              Connexion à BeSpark
            </span>
          </CardTitle>
          <CardDescription>Accédez à votre espace de gestion des feedbacks</CardDescription>
        </CardHeader>
        <CardContent>
          {invitation && (
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                En vous connectant, vous rejoindrez l'organisation <strong>{invitation.organizationName}</strong>
              </AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Votre mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" variant="hero" className="w-full" disabled={signInMutation.isPending}>
              {signInMutation.isPending ? 'Connexion en cours...' : 'Se connecter'}
            </Button>
          </form>
        </CardContent>
        <div className="text-center text-sm text-muted-foreground pb-6">
          Pas encore de compte ?{' '}
          <Link
            to="/register"
            search={invitationToken ? { invitationToken } : undefined}
            className="text-primary hover:underline"
          >
            Creer un compte
          </Link>
        </div>
      </Card>
    </div>
  );
}
